import { Target, TargetCreate, TargetSimple, TargetStatistics, TargetUpdateBasicInfo } from "types";
import { UseMutationOptions, UseQueryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { TargetsAPI } from "api";

const queryKeys = {
  all: () => ["targets"] as const,
  detail: (id: number) => [...queryKeys.all(), id] as const,
  lists: () => [...queryKeys.all(), "list"] as const,
  listAll: () => [...queryKeys.lists(), "all"] as const,
  statistics: (targetId: number | string) => [...queryKeys.all(), "statistics", targetId] as const,
};

/**
 * Fetches a Target by ID.
 */
export const useTarget = <T = Target>(id: number, options: UseQueryOptions<Target, unknown, T> = {}) =>
  useQuery<Target, unknown, T>(queryKeys.detail(id), () => TargetsAPI.get(id), options);

/**
 * Fetches a list of TargetSimple instances - typically used within a select search.
 */
export const useAllTargets = <T = TargetSimple[]>(options: UseQueryOptions<TargetSimple[], unknown, T> = {}) =>
  useQuery<TargetSimple[], unknown, T>(queryKeys.listAll(), TargetsAPI.getAll, options);

export const useTargetStatistics = (
  targetId: number | string,
  options: UseQueryOptions<TargetStatistics, unknown> = {},
) =>
  useQuery<TargetStatistics, unknown>(
    queryKeys.statistics(targetId),
    () => TargetsAPI.getStatistics(targetId),
    options,
  );

/**
 * Mutation to create a new Target.
 */
export const useCreateTarget = (options: UseMutationOptions<Target, unknown, TargetCreate> = {}) =>
  useMutation((data) => TargetsAPI.create(data), options);

/**
 * Mutation to update the Basic Info of a Target.
 *
 * NOTE: This _automatically_ invalidates the cache on success.
 */
export const useUpdateTargetBasicInfo = (options: UseMutationOptions<Target, unknown, TargetUpdateBasicInfo> = {}) => {
  const queryClient = useQueryClient();
  return useMutation(({ id, ...info }) => TargetsAPI.updateBasicInfo(id, info), {
    ...options,
    onSuccess: (response, variables, context) => {
      queryClient.setQueryData(queryKeys.detail(response.targetId), response);
      options.onSuccess?.(response, variables, context);
    },
  });
};

/**
 * Mutation to delete a Target.
 */
export const useDeleteTarget = (options: UseMutationOptions<void, unknown, number> = {}) =>
  useMutation((id) => TargetsAPI.delete(id), options);

type CloneMutation = TargetCreate & { id: number };
/**
 * Clone a target.
 */
export const useCloneTarget = (options: UseMutationOptions<Target, unknown, CloneMutation> = {}) =>
  useMutation(({ id, ...data }) => TargetsAPI.clone(id, data), options);
